/**
 * @fileoverview Custom hook for accessing authentication context
 * Provides a clean interface for authentication state and actions
 * @author SKEMA CANADA
 */

import { useContext } from "react";
import { AuthContext } from "@contexts/AuthContext";

/**
 * Custom hook to access authentication context
 * @returns {Object} Authentication context value
 * @throws {Error} If used outside AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};

/**
 * Hook to check if user is authenticated
 * @returns {boolean} Authentication status
 */
export const useIsAuthenticated = () => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
};

/**
 * Hook to get current user data
 * @returns {Object|null} Current user or null
 */
export const useCurrentUser = () => {
  const { user } = useAuth();
  return user;
};

/**
 * Hook to get authentication loading state
 * @returns {boolean} Loading status
 */
export const useAuthLoading = () => {
  const { loading } = useAuth();
  return loading;
};

/**
 * Hook to get authentication error
 * @returns {string|null} Current error message or null
 */
export const useAuthError = () => {
  const { error } = useAuth();
  return error;
};

/**
 * Hook to get authentication actions
 * @returns {Object} Authentication action methods
 */
export const useAuthActions = () => {
  const { login, logout, clearError } = useAuth();

  return {
    login,
    logout,
    clearError,
  };
};

/**
 * Hook for protected route access control
 * @param {Object} options - Access control options
 * @param {Array<string>} options.requiredPermissions - Required permissions
 * @param {boolean} options.requireAuth - Whether authentication is required
 * @returns {Object} Access control result
 */
export const useRouteAccess = (options = {}) => {
  const { requiredPermissions = [], requireAuth = true } = options;

  const { isAuthenticated, loading } = useAuth();

  // Still loading authentication state
  if (loading) {
    return {
      hasAccess: false,
      loading: true,
      reason: null,
    };
  }

  // Authentication required but user not authenticated
  if (requireAuth && !isAuthenticated) {
    return {
      hasAccess: false,
      loading: false,
      reason: "AUTHENTICATION_REQUIRED",
    };
  }

  // Check permissions if specified
  if (requiredPermissions.length > 0) {
    return {
      hasAccess: false,
      loading: false,
      reason: "INSUFFICIENT_PERMISSIONS",
    };
  }

  return {
    hasAccess: true,
    loading: false,
    reason: null,
  };
};